/**
 * Rozbieżności między starym WF a kartoteką — panel ITE (ADR-0112b, pakiet 8).
 *
 * ── TO JEST PODGLĄD, NIE ROZSTRZYGANIE ──
 *
 * Pytanie „kontrahent Kowalski, e-mail: u nas jeden, w starym WF inny — która wartość jest
 * prawdziwa" to pytanie o dane klienta. Odpowiedź zna osoba, która ten rekord edytowała, a nie
 * wdrożeniowiec ITE. Rozstrzyganie mieszka więc w panelu tenanta; tutaj widać tę samą listę,
 * żeby dało się pomóc przez telefon i zobaczyć skalę rozjazdu.
 *
 * Dlatego w tym oknie nie ma ani jednego przycisku zapisującego decyzję — i jest to zabezpieczenie,
 * nie brak funkcji.
 *
 * ── WYKRYWANIE NICZEGO NIE ZMIENIA ──
 *
 * Czyta obie strony i punkt odniesienia, zapisuje wyłącznie listę pytań. Ani kartoteka klienta,
 * ani jego stara baza nie są ruszane — dlatego to okno nie ma „próby na sucho": ono całe jest
 * na sucho.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ScanSearch } from 'lucide-react';

import { Alert } from '@/components/Alert';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { RadioGroup } from '@/components/RadioGroup';
import { Skeleton } from '@/components/Skeleton';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';

const KARTOTEKI = ['contractor', 'employee'] as const;
type Kartoteka = (typeof KARTOTEKI)[number];

interface ConflictRow {
  id: string;
  entity: string;
  sourceRecordId: string;
  recordId: string | null;
  targetKind: string;
  targetField: string;
  sourceField: string;
  ourValue: string | null;
  theirValue: string | null;
  baselineValue: string | null;
  detectedAt: string;
}

interface DetectReport {
  processed: number;
  unlinked: number;
  inAgreement: number;
  conflicted: number;
  conflictFields: number;
  pendingToUs: number;
  pendingToThem: number;
  wentStale: number;
  warnings: string[];
}

export interface LegacyConflictsModalProps {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  migrationId: string;
  source: { id: string; name: string } | null;
}

export function LegacyConflictsModal({
  open,
  onClose,
  tenantId,
  migrationId,
  source,
}: LegacyConflictsModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [entity, setEntity] = useState<Kartoteka>('contractor');
  const [report, setReport] = useState<DetectReport | null>(null);

  const sciezka = source
    ? `/admin/tenants/${tenantId}/legacy-migration/${migrationId}/sources/${source.id}/conflicts`
    : null;
  const queryKey = [sciezka, entity];

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: async () =>
      (await api.get<{ conflicts: ConflictRow[]; total: number }>(`${sciezka}?entity=${entity}`))
        .data,
    enabled: open && sciezka !== null,
  });

  const wykryj = useMutation({
    mutationFn: async () => (await api.post<DetectReport>(sciezka!, { entity })).data,
    onSuccess: (res) => {
      setReport(res);
      void queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) =>
      toast.show({
        variant: 'error',
        title: t(
          `admin.tenants.legacyMigration.errors.${extractErrorCode(err)}`,
          t('admin.tenants.legacyMigration.conflicts.detectFailed'),
        ),
      }),
  });

  const zamknij = () => {
    setReport(null);
    onClose();
  };

  const zmienKartoteke = (nowa: Kartoteka) => {
    setEntity(nowa);
    setReport(null);
  };

  return (
    <Modal
      open={open}
      onClose={zamknij}
      size="xl"
      closeOnBackdrop={!wykryj.isPending}
      closeOnEsc={!wykryj.isPending}
      title={t('admin.tenants.legacyMigration.conflicts.title', { name: source?.name ?? '' })}
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-[var(--text-secondary)]">
          {t('admin.tenants.legacyMigration.conflicts.intro')}
        </p>

        <Alert tone="info">{t('admin.tenants.legacyMigration.conflicts.whoDecides')}</Alert>

        <RadioGroup<Kartoteka>
          name="legacy-conflicts-entity"
          legend={t('admin.tenants.legacyMigration.run.entityLegend')}
          value={entity}
          onChange={zmienKartoteke}
          options={KARTOTEKI.map((k) => ({
            value: k,
            label: t(`admin.tenants.legacyMigration.run.entity.${k}`),
          }))}
          data-testid="legacy-conflicts-entity"
        />

        {report && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">
              {t('admin.tenants.legacyMigration.conflicts.result', { count: report.processed })}
            </span>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
              {(
                [
                  ['conflictFields', report.conflictFields],
                  ['conflicted', report.conflicted],
                  ['inAgreement', report.inAgreement],
                  ['pendingToUs', report.pendingToUs],
                  ['pendingToThem', report.pendingToThem],
                  ['unlinked', report.unlinked],
                  ['wentStale', report.wentStale],
                ] as const
              )
                .filter(([, ile]) => ile > 0)
                .map(([klucz, ile]) => (
                  <div key={klucz} className="flex justify-between gap-4">
                    <dt className="text-[var(--text-secondary)]">
                      {t(`admin.tenants.legacyMigration.conflicts.stat.${klucz}`)}
                    </dt>
                    <dd className="font-medium">{ile}</dd>
                  </div>
                ))}
            </dl>
            {report.warnings.map((w) => (
              <Alert key={w} tone="warning">
                {w}
              </Alert>
            ))}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-medium">
              {t('admin.tenants.legacyMigration.conflicts.listTitle')}
            </span>
            {data && <Badge color={data.total > 0 ? 'amber' : 'gray'}>{data.total}</Badge>}
          </div>

          {isLoading && <Skeleton className="h-24 w-full" />}

          {data && data.conflicts.length === 0 && (
            <span className="text-sm text-[var(--text-secondary)]">
              {t('admin.tenants.legacyMigration.conflicts.empty')}
            </span>
          )}

          {data?.conflicts.map((k) => (
            <div
              key={k.id}
              className="flex flex-col gap-1 rounded-md border border-[var(--border-primary)] p-3"
            >
              <div className="flex items-baseline gap-2">
                <span className="text-sm font-medium">{k.targetField}</span>
                <span className="text-xs text-[var(--text-secondary)]">
                  {t('admin.tenants.legacyMigration.conflicts.record', {
                    id: k.sourceRecordId,
                    field: k.sourceField,
                  })}
                </span>
              </div>
              <dl className="grid grid-cols-3 gap-2 text-sm">
                <Wartosc
                  etykieta={t('admin.tenants.legacyMigration.conflicts.ours')}
                  wartosc={k.ourValue}
                />
                <Wartosc
                  etykieta={t('admin.tenants.legacyMigration.conflicts.theirs')}
                  wartosc={k.theirValue}
                />
                <Wartosc
                  etykieta={t('admin.tenants.legacyMigration.conflicts.baseline')}
                  wartosc={k.baselineValue}
                />
              </dl>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={zamknij}>
            {t('common.close')}
          </Button>
          <Button
            leftIcon={<ScanSearch className="size-4" />}
            onClick={() => wykryj.mutate()}
            disabled={wykryj.isPending}
          >
            {t('admin.tenants.legacyMigration.conflicts.detect')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Jedna z trzech wartości konfliktu. Puste pokazujemy jawnie — „brak" też jest odpowiedzią. */
function Wartosc({ etykieta, wartosc }: { etykieta: string; wartosc: string | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex min-w-0 flex-col">
      <dt className="text-xs text-[var(--text-secondary)]">{etykieta}</dt>
      <dd className="truncate">
        {wartosc ?? (
          <span className="text-[var(--text-secondary)]">
            {t('admin.tenants.legacyMigration.conflicts.emptyValue')}
          </span>
        )}
      </dd>
    </div>
  );
}
